import {
  CandidatesConst,
  CandidateOpportunitiesConst,
  AxiomEmployeeType,
  CandidateSearchFilters,
} from '@axiom/const';
import { arrayToConstMap, CandidateProfileUtils } from '@axiom/utils';
import type {
  BarredLocation,
  Candidate,
  CandidateOpportunity,
  EnvoyCandidateSearchFilters,
  MLRecommendation,
  Opportunity,
  OpportunityCandidate,
  User,
} from '@axiom/validation';
import qs from 'qs';
import type { BadgeBackgroundType } from '@axiom/ui';

import selectifyUtil from './selectify-util';

// States that allow remote work are considered "green states"
export const statesAllowingRemoteWork = [
  'Arizona',
  'California',
  'Colorado',
  'Connecticut',
  'Florida',
  'Georgia',
  'Hawaii',
  'Illinois',
  'Kentucky',
  'Maine',
  'Maryland',
  'Michigan',
  'Minnesota',
  'Montana',
  'New Jersey',
  'New York',
  'North Carolina',
  'Ohio',
  'South Carolina',
  'Tennessee',
  'Texas',
  'Utah',
  'Virginia',
  'Washington',
  'Wisconsin',
];

export type ProactiveBadgeStatus = 'open' | 'closed' | 'not-suitable';

// Mutually-exclusive outcome for the open/download-bio flows. Distinct from
// ProactiveBadgeStatus: its 'not-suitable' splits here into 'skip' (NoBio
// statuses) vs 'anonymous' (eligible-but-unshared + non-eligible-non-NoBio).
export type BioDisposition = 'skip' | 'named' | 'anonymous';

export interface CrossFulfillmentCounts {
  selected: number;
  interviewing: number;
  submitted: number;
  optedIn: number;
  total: number;
}

export const PROACTIVE_BADGE_CONFIG: Record<
  ProactiveBadgeStatus,
  { background: 'light gray' | 'red'; label: string }
> = {
  open: { background: 'light gray', label: 'Proactive - Open' },
  closed: { background: 'red', label: 'Proactive - Not Open' },
  'not-suitable': { background: 'red', label: 'Proactive - Not Suitable' },
};

const {
  ProfileStatusLists,
  SortedProfileStatuses,
  ProactiveEligibleStatuses,
  NoBioStatuses,
} = CandidatesConst;

const { CandidateStatuses, MLRecommendationStatuses } =
  CandidateOpportunitiesConst;
type ProfileStatusListsType = {
  [key: string]: {
    name: string;
    value: [string];
  };
};

const profileStatusLists = Object.keys(
  ProfileStatusLists
).reduce<ProfileStatusListsType>(
  // @ts-expect-error Needs new type
  (acc, k: 'allCertifiedTalent' | 'allProspects') => ({
    ...acc,
    [k]: {
      label: ProfileStatusLists[k].name,
      value: ProfileStatusLists[k].value,
    },
  }),
  {}
);

const RemovedStatuses = new Set([
  CandidateStatuses.Cooled,
  CandidateStatuses.InterestedRejected,
  CandidateStatuses.Removed,
  CandidateStatuses.TalentOptOut,
  MLRecommendationStatuses.AiRemoved,
]);

const CrossFulfillStatuses = new Set([
  CandidateStatuses.Selected,
  CandidateStatuses.Interviewing,
  CandidateStatuses.Submitted,
  CandidateStatuses.WarmedYes,
]);

export const CandidateUtil = {
  getCandidatesListFilterDefaults() {
    return {
      expandFa: [CandidateSearchFilters.expandFa.PRIMARY],
      axiomEmployeeType: [AxiomEmployeeType.Insourcing],
      profileStatus: [
        // @ts-expect-error Needs new type
        ...CandidateUtil.profileStatusLists.allCertifiedTalent.value,
      ],
    };
  },
  getCandidatesListFilters() {
    const currentQueryParams = qs.parse(window.location.search.slice(1));
    return Object.keys(currentQueryParams).length > 0
      ? currentQueryParams
      : CandidateUtil.getCandidatesListFilterDefaults();
  },
  hasMinYearsSelection({
    filters,
    name,
  }: {
    filters: Partial<
      Pick<
        EnvoyCandidateSearchFilters,
        'industries' | 'legalSkills' | 'practiceAreasAndFocusAreas'
      >
    >;
    name:
      'industriesMinYears' | 'legalSkillsMinYears' | 'practiceAreasMinYears';
  }) {
    switch (name) {
      case 'industriesMinYears': {
        return !!filters.industries?.length;
      }
      case 'legalSkillsMinYears': {
        return !!filters.legalSkills?.length;
      }
      default: {
        return !!(
          filters.practiceAreasAndFocusAreas?.practiceAreas?.length ||
          filters.practiceAreasAndFocusAreas?.focusAreas?.length
        );
      }
    }
  },
  profileFilterStatusOptions: [
    {
      label: ProfileStatusLists.allCertifiedTalent.name,
      value: ProfileStatusLists.allCertifiedTalent.value,
    },
    {
      label: ProfileStatusLists.allProspects.name,
      value: ProfileStatusLists.allProspects.value,
    },
    ...SortedProfileStatuses.map(k => ({
      label: k,
      value: k,
    })),
  ],
  getProfileStatusSelectItems() {
    const profileStatusGroup = 'filters.profileStatus';
    const profileStatusGroupLabel = 'Profile Status';
    return [
      {
        group: profileStatusGroup,
        groupLabel: profileStatusGroupLabel,
        // @ts-expect-error Needs new type
        ...profileStatusLists.allCertifiedTalent,
      },
      {
        group: profileStatusGroup,
        groupLabel: profileStatusGroupLabel,
        // @ts-expect-error Needs new type
        ...profileStatusLists.allProspects,
      },
      ...selectifyUtil({
        group: profileStatusGroup,
        groupLabel: profileStatusGroupLabel,
        options: arrayToConstMap(SortedProfileStatuses),
        sort: null,
      }),
    ];
  },
  isSubmittedFromWorkFeed: (
    candidateOpportunity: CandidateOpportunity | OpportunityCandidate,
    opportunity?: Partial<Opportunity> | null
  ) => {
    return (
      !!opportunity?.selfService &&
      candidateOpportunity.candidateStatus === CandidateStatuses.Submitted &&
      candidateOpportunity.previousCandidateStatus ===
        CandidateStatuses.Interested
    );
  },
  profileStatusLists,
  setConfidentialExperiences(candidate: Candidate, isAnon = false): Candidate {
    const data = { ...candidate };

    if (Array.isArray(candidate.experiences)) {
      data.experiences = candidate.experiences.map(exp => {
        if (!isAnon && exp.opportunity?.account?.isAccountConfidential) {
          exp.client = exp.opportunity.account.calculatedConfidentialName || '';
        } else if (exp.isConfidential || isAnon) {
          exp.client = exp.isAxiom ? 'Axiom Client' : 'Confidential Company';
        }

        return exp;
      });
    }

    return data;
  },
  formatYOE(years: string): string {
    const numYears = Number.parseInt(years, 10);
    let suffix = ' year';
    let prefix = '';

    if (numYears < 1) {
      prefix = '< ';
    } else if (numYears < 2) {
      suffix = '+ year';
    }
    if (numYears >= 2) suffix = '+ years';

    return `${prefix}${numYears >= 2 ? numYears : 1}${suffix}`;
  },
  getCandidateStatusBadgeColor(
    candidateStatus: CandidateOpportunity['candidateStatus']
  ): BadgeBackgroundType {
    switch (candidateStatus) {
      case CandidateStatuses.WarmedYes:
      case CandidateStatuses.Submitted:
      case CandidateStatuses.Interviewing: {
        return 'sky';
      }

      case CandidateStatuses.Selected:
      case CandidateStatuses.Engaged: {
        return 'green';
      }

      default: {
        return 'gray';
      }
    }
  },
  getProactiveBadgeStatus(
    profileStatus: Candidate['profileStatus'],
    isProfileShared: Candidate['isProfileShared']
  ): ProactiveBadgeStatus {
    if (!profileStatus || !ProactiveEligibleStatuses.has(profileStatus)) {
      return 'not-suitable';
    }
    return isProfileShared ? 'open' : 'closed';
  },
  getBioDisposition(
    candidate: Pick<Candidate, 'profileStatus' | 'isProfileShared'>
  ): BioDisposition {
    if (NoBioStatuses.has(candidate.profileStatus)) {
      return 'skip';
    }
    return CandidateProfileUtils.isProactiveOpen(candidate)
      ? 'named'
      : 'anonymous';
  },
  getProfileStatusEditorName(
    candidate: Pick<
      Candidate,
      'profileStatusLastUpdatedBy' | 'profileStatusLastUpdatedByUser'
    >,
    usersById: Record<User['id'], string | null>
  ): string {
    if (candidate.profileStatusLastUpdatedBy) {
      const userName = usersById[candidate.profileStatusLastUpdatedBy];
      return (
        candidate.profileStatusLastUpdatedByUser?.fullName ??
        userName ??
        'Profile Default'
      );
    }
    return 'Profile Default';
  },
  filterRemovedCandidates: (
    opportunityCandidates: Array<OpportunityCandidate | CandidateOpportunity>
  ): Array<OpportunityCandidate | CandidateOpportunity> => {
    return opportunityCandidates.filter(
      c => !RemovedStatuses.has(c.candidateStatus)
    );
  },
  getRemovedCandidates: (
    allCandidates: Array<OpportunityCandidate | CandidateOpportunity>
  ): Array<OpportunityCandidate | CandidateOpportunity> => {
    return allCandidates.filter(c => RemovedStatuses.has(c.candidateStatus));
  },
  filterRemovedMLRecommendations: (
    mlRecommendations: MLRecommendation[]
  ): MLRecommendation[] => {
    return mlRecommendations.filter(
      rec => !RemovedStatuses.has(rec.candidateStatus)
    );
  },
  getRemovedMLRecommendations: (
    mlRecommendations: MLRecommendation[]
  ): MLRecommendation[] => {
    return mlRecommendations.filter(rec =>
      RemovedStatuses.has(rec.candidateStatus)
    );
  },
  getCrossFulfillmentRelevantOpportunities(
    candidateOpportunities: CandidateOpportunity[]
  ): CandidateOpportunity[] {
    return candidateOpportunities.filter(co =>
      CrossFulfillStatuses.has(co.candidateStatus)
    );
  },
  getCrossFulfillmentCounts(
    candidateOpportunities: CandidateOpportunity[]
  ): CrossFulfillmentCounts {
    const relevantOpportunities = this.getCrossFulfillmentRelevantOpportunities(
      candidateOpportunities
    );

    const countByStatus = (status: string): number =>
      relevantOpportunities.filter(
        (co: CandidateOpportunity) => co.candidateStatus === status
      ).length;

    return {
      selected: countByStatus(CandidateStatuses.Selected),
      interviewing: countByStatus(CandidateStatuses.Interviewing),
      submitted: countByStatus(CandidateStatuses.Submitted),
      optedIn: countByStatus(CandidateStatuses.WarmedYes),
      total: relevantOpportunities.length,
    };
  },
  formatCrossFulfillmentCountsMessage(
    counts: CrossFulfillmentCounts
  ): string[] {
    const messages: string[] = [];

    if (counts.selected > 0) {
      messages.push(`${counts.selected} Selected`);
    }
    if (counts.interviewing > 0) {
      messages.push(`${counts.interviewing} Interviewing`);
    }
    if (counts.submitted > 0) {
      messages.push(`${counts.submitted} Submitted`);
    }
    if (counts.optedIn > 0) {
      messages.push(`${counts.optedIn} Opted In`);
    }

    return messages;
  },
  isGreenState: (state: string): boolean => {
    return statesAllowingRemoteWork.includes(state);
  },
  isBarredInState: (
    barredLocations: BarredLocation[] | null | undefined,
    state: string
  ): boolean => {
    return (
      !!barredLocations &&
      !!state &&
      barredLocations.some(
        location => location.addressState?.toLowerCase() === state.toLowerCase()
      )
    );
  },
};
